import type { AppSettings, Category, RecurringExpense, SavingsGoal, Tag, Transaction } from '@/lib/types';
import { DEFAULT_APP_SETTINGS } from '@/lib/appSettings';
import { getDb } from '@/lib/db';
import { normalizeCurrency } from '@/lib/money';

export type BackupPayload = {
  categories: Category[];
  tags: Tag[];
  transactions: Transaction[];
  recurringExpenses: RecurringExpense[];
  savingsGoals: SavingsGoal[];
  settings: AppSettings;
};

export type ImportPayload = BackupPayload & {
  ignoredCount: number;
};

const REQUIRED_IMPORT_KEYS = ['categories', 'tags', 'transactions', 'recurringExpenses', 'savingsGoals'] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function isOptionalString(value: unknown): value is string | undefined {
  return value === undefined || typeof value === 'string';
}

function isNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function uniqueById<T extends { id: string }>(items: T[]) {
  const seen = new Set<string>();
  const unique: T[] = [];
  let duplicateCount = 0;

  items.forEach((item) => {
    if (seen.has(item.id)) {
      duplicateCount += 1;
      return;
    }
    seen.add(item.id);
    unique.push(item);
  });

  return { unique, duplicateCount };
}

function sanitizeCategories(items: unknown[]) {
  const valid = items.filter((item): item is Category => {
    if (!isRecord(item)) return false;
    return (
      isString(item.id) &&
      isString(item.name) &&
      (item.monthlyBudget === undefined || isNumber(item.monthlyBudget)) &&
      isOptionalString(item.color) &&
      isOptionalString(item.icon) &&
      isString(item.createdAt) &&
      isString(item.updatedAt)
    );
  });
  return uniqueById(valid);
}

function sanitizeTags(items: unknown[]) {
  const valid = items.filter((item): item is Tag => {
    if (!isRecord(item)) return false;
    return isString(item.id) && isString(item.name) && typeof item.categoryId === 'string' && isString(item.createdAt) && isString(item.updatedAt);
  });
  return uniqueById(valid);
}

function sanitizeTransactions(items: unknown[]) {
  const valid = items.filter((item): item is Transaction => {
    if (!isRecord(item)) return false;
    return (
      isString(item.id) &&
      isNumber(item.amount) &&
      item.amount > 0 &&
      (item.type === 'income' || item.type === 'expense') &&
      isString(item.date) &&
      isOptionalString(item.categoryId) &&
      Array.isArray(item.tagIds) &&
      item.tagIds.every((tagId) => typeof tagId === 'string') &&
      isOptionalString(item.note) &&
      isString(item.createdAt) &&
      isString(item.updatedAt)
    );
  });
  return uniqueById(valid);
}

function sanitizeRecurringExpenses(items: unknown[]) {
  const valid = items.filter((item): item is RecurringExpense => {
    if (!isRecord(item)) return false;
    return (
      isString(item.id) &&
      isString(item.name) &&
      isNumber(item.amount) &&
      item.amount > 0 &&
      isOptionalString(item.categoryId) &&
      (item.dayOfMonth === undefined || (isNumber(item.dayOfMonth) && item.dayOfMonth >= 1 && item.dayOfMonth <= 31)) &&
      typeof item.active === 'boolean' &&
      isString(item.createdAt) &&
      isString(item.updatedAt)
    );
  });
  return uniqueById(valid);
}

function sanitizeSavingsGoals(items: unknown[]) {
  const valid = items.filter((item): item is SavingsGoal => {
    if (!isRecord(item)) return false;
    return isString(item.id) && isString(item.month) && isNumber(item.amount) && item.amount >= 0 && isString(item.createdAt) && isString(item.updatedAt);
  });
  return uniqueById(valid);
}

function sanitizeSettings(item: unknown): AppSettings {
  if (!isRecord(item)) return DEFAULT_APP_SETTINGS;

  return {
    id: 'main',
    currency: normalizeCurrency(typeof item.currency === 'string' ? item.currency : undefined),
    createdAt: typeof item.createdAt === 'string' ? item.createdAt : '',
    updatedAt: typeof item.updatedAt === 'string' ? item.updatedAt : '',
  };
}

export function validateImportPayload(data: unknown): ImportPayload {
  if (!isRecord(data)) {
    throw new Error('La sauvegarde doit contenir un objet JSON.');
  }

  const missingKey = REQUIRED_IMPORT_KEYS.find((key) => !Array.isArray(data[key]));
  if (missingKey) {
    throw new Error(`La sauvegarde est incomplète: "${missingKey}" est manquant ou invalide.`);
  }

  const categories = sanitizeCategories(data.categories as unknown[]);
  const tags = sanitizeTags(data.tags as unknown[]);
  const transactions = sanitizeTransactions(data.transactions as unknown[]);
  const recurringExpenses = sanitizeRecurringExpenses(data.recurringExpenses as unknown[]);
  const savingsGoals = sanitizeSavingsGoals(data.savingsGoals as unknown[]);
  const settings = sanitizeSettings(data.settings);
  const ignoredCount =
    (data.categories as unknown[]).length - categories.unique.length +
    (data.tags as unknown[]).length - tags.unique.length +
    (data.transactions as unknown[]).length - transactions.unique.length +
    (data.recurringExpenses as unknown[]).length - recurringExpenses.unique.length +
    (data.savingsGoals as unknown[]).length - savingsGoals.unique.length;

  return {
    categories: categories.unique,
    tags: tags.unique,
    transactions: transactions.unique,
    recurringExpenses: recurringExpenses.unique,
    savingsGoals: savingsGoals.unique,
    settings,
    ignoredCount,
  };
}

export async function createBackupPayload(): Promise<BackupPayload> {
  const db = getDb();
  if (!db) {
    throw new Error('Le stockage local est indisponible sur cet appareil.');
  }

  const categories = await db.categories.toArray();
  const tags = await db.tags.toArray();
  const transactions = await db.transactions.toArray();
  const recurringExpenses = await db.recurringExpenses.toArray();
  const savingsGoals = await db.savingsGoals.toArray();
  const settings = (await db.settings.get('main')) ?? DEFAULT_APP_SETTINGS;

  return { categories, tags, transactions, recurringExpenses, savingsGoals, settings };
}

export async function restoreBackupPayload(data: ImportPayload) {
  const db = getDb();
  if (!db) {
    throw new Error('Le stockage local est indisponible sur cet appareil.');
  }

  await db.transaction('rw', [db.categories, db.tags, db.transactions, db.recurringExpenses, db.savingsGoals, db.settings], async () => {
    await db.categories.clear();
    await db.tags.clear();
    await db.transactions.clear();
    await db.recurringExpenses.clear();
    await db.savingsGoals.clear();
    await db.settings.clear();
    await db.categories.bulkAdd(data.categories);
    await db.tags.bulkAdd(data.tags);
    await db.transactions.bulkAdd(data.transactions);
    await db.recurringExpenses.bulkAdd(data.recurringExpenses);
    await db.savingsGoals.bulkAdd(data.savingsGoals);
    await db.settings.put({
      ...data.settings,
      createdAt: data.settings.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
  });
}

export function backupSummary(data: ImportPayload) {
  return `${data.categories.length} catégorie(s), ${data.tags.length} tag(s), ${data.transactions.length} transaction(s), ${data.recurringExpenses.length} dépense(s) récurrente(s) et ${data.savingsGoals.length} objectif(s)`;
}
